<!--
  z-[1100]: intentionally above ordinary modals/LoadingPopupComponent (z-[999])
  so a toast confirming an action is never hidden behind them, but the toast
  itself stays a small, corner-positioned, non-blocking element so it never
  visually competes with the full-screen blocking SessionExpired dialog
  (z-[1000]) even when both happen to be present.

  pointer-events-none on this wrapper + pointer-events-auto on each toast
  card: only the toast cards themselves can intercept clicks, so the stack
  never blocks interaction with the page behind it.
-->
<div
  class="fixed top-4 right-4 z-[1100] flex flex-col gap-2 w-[calc(100%-2rem)] max-w-sm pointer-events-none"
>
  <div
    *ngFor="let toast of toasts$ | async"
    class="toast-enter pointer-events-auto bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden border-l-4"
    [ngClass]="{
      'border-l-green-500': toast.type === 'success',
      'border-l-red-500': toast.type === 'error',
      'border-l-amber-500': toast.type === 'warning',
      'border-l-blue-500': toast.type === 'info'
    }"
  >
    <div class="flex items-start gap-3 p-3">
      <div class="shrink-0 mt-0.5" [ngSwitch]="toast.type">
        <svg
          *ngSwitchCase="'success'"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="10" fill="#DCFCE7" />
          <path
            d="M8 12.5l2.5 2.5L16 9.5"
            stroke="#16A34A"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <svg
          *ngSwitchCase="'error'"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="10" fill="#FEE2E2" />
          <path
            d="M9 9l6 6M15 9l-6 6"
            stroke="#DC2626"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
        <svg
          *ngSwitchCase="'warning'"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="10" fill="#FEF3C7" />
          <path
            d="M12 7.5v5.5M12 16h.01"
            stroke="#D97706"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
        <svg
          *ngSwitchCase="'info'"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
          <path
            d="M12 8h.01M12 11v5"
            stroke="#2563EB"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <div class="flex-1 min-w-0">
        <div class="flex items-start justify-between gap-2">
          <h3 class="text-[0.78rem] font-semibold text-gray-900">
            {{ getTitle(toast.type) }}
          </h3>
          <button
            type="button"
            class="shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer"
            (click)="close(toast.id)"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19.1471 18.3525C19.2024 18.404 19.2467 18.4661 19.2775 18.5351C19.3082 18.6041 19.3248 18.6786 19.3261 18.7541C19.3274 18.8296 19.3135 18.9047 19.2852 18.9747C19.2569 19.0447 19.2148 19.1084 19.1614 19.1618C19.108 19.2152 19.0444 19.2573 18.9743 19.2856C18.9043 19.3139 18.8293 19.3278 18.7537 19.3265C18.6782 19.3251 18.6037 19.3086 18.5347 19.2778C18.4657 19.2471 18.4036 19.2028 18.3521 19.1475L11.9996 12.7959L5.64714 19.1475C5.54051 19.2469 5.39947 19.301 5.25375 19.2984C5.10802 19.2958 4.96898 19.2368 4.86592 19.1337C4.76286 19.0307 4.70383 18.8916 4.70126 18.7459C4.69869 18.6002 4.75278 18.4591 4.85214 18.3525L11.2037 12L4.85214 5.64751C4.75278 5.54088 4.69869 5.39984 4.70126 5.25411C4.70383 5.10839 4.76286 4.96935 4.86592 4.86629C4.96898 4.76323 5.10802 4.7042 5.25375 4.70163C5.39947 4.69905 5.54051 4.75315 5.64714 4.85251L11.9996 11.2041L18.3521 4.85251C18.4588 4.75315 18.5998 4.69905 18.7455 4.70163C18.8913 4.7042 19.0303 4.76323 19.1334 4.86629C19.2364 4.96935 19.2955 5.10839 19.298 5.25411C19.3006 5.39984 19.2465 5.54088 19.1471 5.64751L12.7956 12L19.1471 18.3525Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
        <p class="text-[0.72rem] text-gray-500 mt-0.5 break-words">
          {{ toast.message }}
        </p>
      </div>
    </div>
  </div>
</div>
